import type { OngoingDownload } from '@mimir/client-common/model/ongoingDownload';
import { TransferStateDto } from '@mimir/common/dto/transferRequestDtos';
import { importToPremiereAsync } from './importToPremiere';
import { openProjectFile } from './premiereProject';
import { updateExistingClip } from './updateExistingClip';
import { type AdobePanelContext, type ElectronContext, isInPremiere } from '@mimir/common/context/applicationContext';
import { convertSecondsToTicks } from '@mimir/client-common/util/premiere';
import type { NotificationOps } from '@mimir/client-common/fileSync/notificationOps';
import type { Logger } from '@mimir/common/types/logger';
import { invariant } from '@mimir/common/util/invariant';
import { getGlobalStore } from '../store';
import type { LocalStateOps } from '@mimir/client-common/fileSync/localStateOps';

export async function completeAndImport(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
    localStateOps: LocalStateOps,
  },
  config: {
    downloadPath: string,
    userDataSystemPath: string,
    shouldUsePremierePreferences: boolean,
  },
  ongoingDownload: OngoingDownload,
  // The path that has been verified as being locally available
  locallyAvailablePath: string,
) {
  // File available since last checked. Import and mark completion.
  ongoingDownload.transferState = TransferStateDto.COMPLETE;

  if (ongoingDownload.postDownloadHooks !== undefined) {
    await Promise.all(ongoingDownload.postDownloadHooks.map(h => h(locallyAvailablePath)));
  }

  if (!ongoingDownload.importAfterCompletion) {
    if (isInPremiere(services.applicationContext) || getGlobalStore().getters.dvrMode) {
      await updateExistingClip(services, ongoingDownload, locallyAvailablePath);
    }

    return true;
  }

  const title = ongoingDownload.title || '';

  if (ongoingDownload.downloadDestination?.type === 'builtin-project-file-download') {
    invariant(ongoingDownload.itemId !== undefined, 'Expected itemId for built-in project file download');
    await openProjectFile(services, config, {
      projectOriginalFileName: ongoingDownload.originalFileName || '',
      filePath: locallyAvailablePath,
      title,
      itemId: ongoingDownload.itemId,
      suppressWarning: false,
      isProjectTemplate: Boolean(ongoingDownload.isProjectTemplate),
    });
    return true;
  }

  await importToPremiereAsync(
    services.applicationContext,
    {
      filePath: locallyAvailablePath,
      title,
      subclipTitle: ongoingDownload.subclip?.title,
      subclipId: ongoingDownload.subclip?.id,
      startTime: ongoingDownload.subclip?.startTime,
      endTime: ongoingDownload.subclip?.endTime,
      sourceItemId: ongoingDownload.isSubclipItem ? '' : ongoingDownload.itemId,
      proxyFilePath: ongoingDownload.artefact === 'proxy' ? locallyAvailablePath : undefined,
      tapeName: ongoingDownload.originalFileName,
      artifactReference: ongoingDownload.artifactReference ?? null,
      insertIntoSequence: ongoingDownload.insertIntoSequence,
      markers: ongoingDownload.markers,
      startTimeInTicks: ongoingDownload.premiereStartTimeSeconds ? convertSecondsToTicks(ongoingDownload.premiereStartTimeSeconds) : null,
      timeDisplayFormat: ongoingDownload.premiereTimeDisplayFormat ?? null,
      mediaFrameRate: ongoingDownload.mediaFrameRate,
      mogrtChildrenMap: ongoingDownload.mogrtChildrenMap,
      timelineInsertion: ongoingDownload.timelineInsertion ?? null,
    },
    config.shouldUsePremierePreferences,
    ongoingDownload.targetBin,
  );

  return true;
}
